import { useNavigate } from 'react-router-dom';
import { User, HelpCircle, LogOut, Shield, TrendingUp, Building, LayoutDashboard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useCurrentUser, useLogout } from '@/lib/auth';
import { ROUTES, getPrimaryUserType } from '@/config/routes';
import { supabase } from '@/integrations/supabase/client';
import { useUserProfile } from '@/features/profile/api/get-user-profile';
import type { UserType } from '@/features/profile/api/types';

/**
 * User menu dropdown component
 * Displays user info and provides access to role-appropriate profile, settings, help, and sign out
 * Menu items adapt based on user's role (learner, employer, platform)
 */
export const UserMenu = () => {
  const navigate = useNavigate();
  const { user, session, email, userTypes, isLearner, isEmployer, isPlatform } = useCurrentUser();
  const logout = useLogout();

  // Get primary user type (priority: platform > employer > learner)
  const primaryUserType = getPrimaryUserType(userTypes || []);

  const userId = user?.id ?? '';
  const userType = (userTypes?.[0] as UserType) ?? 'learner';

  const { data: profile } = useUserProfile({
    userId,
    userType,
    queryConfig: {
      enabled: !!userId && !!userType,
      staleTime: 1000 * 60 * 10, // 10 minutes -- avatar changes rarely
    },
  });

  // Construct avatar URL from profile data
  const avatarUrl = profile?.file_uploads?.file_path
    ? supabase.storage
        .from(profile.file_uploads.bucket_name)
        .getPublicUrl(profile.file_uploads.file_path).data.publicUrl
    : undefined;

  const getUserDisplayName = () => {
    return user?.user_metadata?.name || email?.split('@')[0] || 'User';
  };

  const getUserInitials = () => {
    const name = getUserDisplayName();
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleSignOut = () => {
    logout.mutate(undefined, {
      onSuccess: () => navigate(ROUTES.HOME),
      onError: (error) => console.error('Error signing out:', error)
    });
  };

  // Get role display name based on user types from database
  const getRoleDisplayName = () => {
    switch (primaryUserType) {
      case 'learner':
        return 'Learner';
      case 'employer':
        return 'Employer';
      case 'platform':
        return 'Platform Admin';
      default:
        return 'User';
    }
  };

  // Only render if both user AND session exist (prevents showing menu to unverified users)
  if (!user || !session) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative size-10 rounded-full"
          aria-label={`User menu for ${getUserDisplayName()}`}
        >
          <Avatar className="size-10">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={getUserDisplayName()} />}
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getUserInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" sideOffset={5}>
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
            <div className="mt-1 flex items-center gap-1 text-xs text-primary">
              <Shield className="size-3" />
              <span>{getRoleDisplayName()}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Profile & Settings - All users */}
        <DropdownMenuItem
          onClick={() => navigate(ROUTES.SETTINGS_PROFILE)}
          className="cursor-pointer"
        >
          <User className="mr-2 size-4" />
          <span>Profile & Settings</span>
        </DropdownMenuItem>

        {/* Learner-specific menu items */}
        {isLearner && (
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.LEARNER_PROGRESS)}
            className="cursor-pointer"
          >
            <TrendingUp className="mr-2 size-4" />
            <span>My Progress</span>
          </DropdownMenuItem>
        )}

        {/* Employer-specific menu items */}
        {isEmployer && (
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.EMPLOYER_COMPANY)}
            className="cursor-pointer"
          >
            <Building className="mr-2 size-4" />
            <span>Company Profile</span>
          </DropdownMenuItem>
        )}

        {/* Platform admin-specific menu items */}
        {isPlatform && (
          <DropdownMenuItem
            onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
            className="cursor-pointer"
          >
            <LayoutDashboard className="mr-2 size-4" />
            <span>Admin Dashboard</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Help & Support - All users */}
        <DropdownMenuItem
          onClick={() => navigate(ROUTES.LEARNER_LEARNING_HUB_RESOURCE_LIBRARY)}
          className="cursor-pointer"
        >
          <HelpCircle className="mr-2 size-4" />
          <span>Help & Support</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Sign Out - All users */}
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 size-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
